import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { fetchAddressesAPI, addressToFakeContact } from '../../../api/addresses';
import { fetchContactsAPI } from '../../../api/contacts';
import { selectCustomerById } from '../../../entities/Customer/customerSelectors';
import { siteGroupSelector } from '../../../entities/SiteGroup/siteGroupSelectors';

/**
 * Standalone async function for auto-filling contact/address based on priority logic
 * Can be used by both React hooks and class components
 *
 * Priority order:
 * 1. Primary billing/shipping contact for site group
 * 2. Primary billing/shipping contact for customer
 * 3. Primary billing/shipping address for site group
 * 4. Primary billing/shipping address for customer
 *
 * @param {Object} params
 * @param {number} params.customerId - Customer ID
 * @param {number} params.siteGroupId - Site Group ID
 * @param {string} params.type - 'billing' or 'shipping'
 * @param {string} params.context - 'rma' or 'sales' (determines which primary flags to use)
 * @param {string} params.customerName - Customer display name (for address fallback)
 * @param {string} params.siteGroupName - Site group display name (for address fallback)
 * @param {boolean|null} params.requireCompleteAddress - Whether to filter for complete_address.
 *   null = auto-detect (true for RMA, false for Sales)
 * @returns {Promise<Object|null>} Contact object or null if not found
 */
export const findAutoFillContact = async ({
  customerId,
  siteGroupId,
  type,
  context = 'rma',
  customerName,
  siteGroupName,
  requireCompleteAddress = null, // null = auto-detect based on context
}) => {
  const isBilling = type === 'billing';
  const isRma = context === 'rma';

  // Build the correct filter name based on context and type
  // Backend expects query params: primary_rma_billing, primary_rma_shipping, primary_sales_billing, primary_sales_shipping
  const primaryFilterName = isRma
    ? isBilling
      ? 'primary_rma_billing'
      : 'primary_rma_shipping'
    : isBilling
      ? 'primary_sales_billing'
      : 'primary_sales_shipping';

  // Determine if complete_address is required:
  // - RMA always requires complete address (contacts include address fields)
  // - Sales does NOT require complete_address (contact and address are separate)
  const needsCompleteAddress = requireCompleteAddress !== null ? requireCompleteAddress : isRma;

  try {
    // 1. Try site group primary contact
    if (siteGroupId) {
      const params = {
        site_group: siteGroupId,
        [primaryFilterName]: true,
        not_deleted: true,
        limit: 1,
      };
      if (needsCompleteAddress) {
        params.complete_address = true;
      }

      const siteGroupContacts = await fetchContactsAPI(params);

      const primarySiteGroupContact = siteGroupContacts.results?.[0];
      if (primarySiteGroupContact) {
        return primarySiteGroupContact;
      }
    }

    // 2. Try customer primary contact (customer-level only, no site_group)
    if (customerId) {
      const params = {
        customer: customerId,
        // Note: when only customer is passed (no site_group), backend returns customer-level contacts only
        [primaryFilterName]: true,
        not_deleted: true,
        limit: 1,
      };
      if (needsCompleteAddress) {
        params.complete_address = true;
      }

      const customerContacts = await fetchContactsAPI(params);

      const primaryCustomerContact = customerContacts.results?.[0];
      if (primaryCustomerContact) {
        return primaryCustomerContact;
      }
    }

    // 3. Try site group primary address (matching type first, then any primary)
    if (siteGroupId) {
      const siteGroupAddresses = await fetchAddressesAPI({
        site_group: siteGroupId,
        primary_only: true,
      });

      // First try to find matching billing/shipping type
      let primarySiteGroupAddress = siteGroupAddresses.find(
        a => a.primary_address && a.is_billing === isBilling && a.site_group != null,
      );

      // If no matching type, use any primary address from site group
      if (!primarySiteGroupAddress) {
        primarySiteGroupAddress = siteGroupAddresses.find(a => a.primary_address && a.site_group != null);
      }

      if (primarySiteGroupAddress && (primarySiteGroupAddress.address_1 || primarySiteGroupAddress.city)) {
        const businessName = siteGroupName || `Site Group #${siteGroupId}`;

        const fakeContact = addressToFakeContact(primarySiteGroupAddress, businessName);
        fakeContact._addressScope = 'Site Group';
        return fakeContact;
      }
    }

    // 4. Try customer primary address (matching type first, then any primary)
    if (customerId) {
      const customerAddresses = await fetchAddressesAPI({
        customer: customerId,
        primary_only: true,
      });

      // First try to find matching billing/shipping type
      let primaryCustomerAddress = customerAddresses.find(
        a => a.primary_address && a.is_billing === isBilling && a.site_group == null,
      );

      // If no matching type, use any primary address from customer
      if (!primaryCustomerAddress) {
        primaryCustomerAddress = customerAddresses.find(a => a.primary_address && a.site_group == null);
      }

      if (primaryCustomerAddress && (primaryCustomerAddress.address_1 || primaryCustomerAddress.city)) {
        const businessName = customerName || 'Unknown Customer';

        const fakeContact = addressToFakeContact(primaryCustomerAddress, businessName);
        fakeContact._addressScope = 'Customer';
        return fakeContact;
      }
    }

    // No match found
    return null;
  } catch (err) {
    console.error('findAutoFillContact: Failed to auto-fill:', err);
    return null;
  }
};

/**
 * Hook for auto-filling contact/address based on priority logic
 * Uses the shared findAutoFillContact utility function
 *
 * @param {Object} params
 * @param {number} params.customerId - Customer ID
 * @param {number} params.siteGroupId - Site Group ID
 * @param {string} params.type - 'billing' or 'shipping'
 * @param {string} params.context - 'rma' or 'sales' (determines which primary flags to use)
 * @param {boolean} params.enabled - Whether auto-fill is enabled (default: false)
 * @returns {Object} { autoFilledContact, loading, error, refetch }
 */
export const useAutoFillContact = ({ customerId, siteGroupId, type, context = 'rma', enabled = false }) => {
  const [autoFilledContact, setAutoFilledContact] = useState(null);
  // Start loading as true if enabled, so consumers know to wait
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  // Get customer and site group from Redux for display names
  const customer = useSelector(state => selectCustomerById(customerId)(state));
  const siteGroups = useSelector(siteGroupSelector);

  // Effect to load auto-fill when enabled and IDs are available
  useEffect(() => {
    // Define async function inside effect to avoid stale closures
    const doAutoFill = async () => {
      if (!enabled || !customerId || !siteGroupId) {
        if (!enabled) {
          setLoading(false);
          setHasLoaded(true);
          setAutoFilledContact(null);
        } else {
          // Enabled but missing IDs - still loading
          setLoading(true);
          setHasLoaded(false);
        }
        return;
      }

      setLoading(true);
      setHasLoaded(false);
      setError(null);

      try {
        // Get display names from Redux (captured fresh in this effect)
        const addressSiteGroup = siteGroups?.get(String(siteGroupId));
        const siteGroupName = addressSiteGroup?.get
          ? addressSiteGroup.get('name', `Site Group #${siteGroupId}`)
          : addressSiteGroup?.name || `Site Group #${siteGroupId}`;
        const customerName = customer?.get
          ? customer.get('name', 'Unknown Customer')
          : customer?.name || 'Unknown Customer';

        const result = await findAutoFillContact({
          customerId,
          siteGroupId,
          type,
          context,
          customerName,
          siteGroupName,
        });

        setAutoFilledContact(result);
      } catch (err) {
        console.error('useAutoFillContact: Failed to auto-fill:', err);
        setError(err.message || 'Failed to auto-fill contact');
        setAutoFilledContact(null);
      } finally {
        setLoading(false);
        setHasLoaded(true);
      }
    };

    doAutoFill();
  }, [enabled, customerId, siteGroupId, type, context, customer, siteGroups]);

  // Manual refetch function
  const refetch = useCallback(async () => {
    if (!enabled || !customerId || !siteGroupId) {
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const addressSiteGroup = siteGroups?.get(String(siteGroupId));
      const siteGroupName = addressSiteGroup?.get
        ? addressSiteGroup.get('name', `Site Group #${siteGroupId}`)
        : addressSiteGroup?.name || `Site Group #${siteGroupId}`;
      const customerName = customer?.get
        ? customer.get('name', 'Unknown Customer')
        : customer?.name || 'Unknown Customer';

      const result = await findAutoFillContact({
        customerId,
        siteGroupId,
        type,
        context,
        customerName,
        siteGroupName,
      });

      setAutoFilledContact(result);
      return result;
    } catch (err) {
      console.error('useAutoFillContact: Failed to auto-fill:', err);
      setError(err.message || 'Failed to auto-fill contact');
      setAutoFilledContact(null);
      return null;
    } finally {
      setLoading(false);
      setHasLoaded(true);
    }
  }, [enabled, customerId, siteGroupId, type, context, customer, siteGroups]);

  return {
    autoFilledContact,
    loading,
    error,
    hasLoaded,
    refetch,
  };
};
